<template>
  <b-modal
    :active.sync="showModal"
    trap-focus
    aria-role="dialog"
    scroll="keep"
    aria-modal
    has-modal-card
    @close="closeModal"
  >
    <ValidationObserver ref="observer" v-slot="{ passes }">
      <form @submit.prevent="passes(exportReport)">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Export Playout Schedule</p>
          </header>
          <section class="modal-card-body">
            <!-- Mode: Export from BMS, or Import a BOSS playlist (UB channels only) -->
            <b-field v-if="isUbChannel" label="Source">
              <b-radio-button v-model="mode" native-value="export" type="is-primary">
                <span>Export from BMS</span>
              </b-radio-button>
              <b-radio-button v-model="mode" native-value="import" type="is-primary">
                <span>Import Playlist (BOSS)</span>
              </b-radio-button>
            </b-field>

            <!-- ================= IMPORT (BOSS) ================= -->
            <template v-if="mode === 'import'">
              <p class="help mb-3">
                <b-icon icon="information" size="is-small"></b-icon>
                Upload the nBOSS micro-schedule <strong>CSV</strong> (resolves show names against
                BMS content) and, optionally, the BOSS iTX <strong>.itxml</strong> (supplies the
                SCTE opt-out positions). CSV-only produces an air-ready playlist with no SCTE; add
                the matching XML to place the broadcaster opt-out (type "C") blocks exactly where
                BOSS fires them. Both files must be from the same BOSS export.
              </p>
              <div class="columns">
                <div class="column is-6">
                  <b-field label="nBOSS micro-schedule (.csv) — required">
                    <b-upload v-model="importCsvFile" accept=".csv" drag-drop expanded>
                      <section class="section">
                        <div class="content has-text-centered">
                          <p><b-icon icon="file-delimited" size="is-large"></b-icon></p>
                          <p>{{ importCsvFile ? importCsvFile.name : 'Drop the BOSS .csv here or click to browse' }}</p>
                        </div>
                      </section>
                    </b-upload>
                  </b-field>
                </div>
                <div class="column is-6">
                  <b-field label="BOSS iTX SCTE (.itxml) — optional">
                    <b-upload v-model="importXmlFile" accept=".itxml,.xml" drag-drop expanded>
                      <section class="section">
                        <div class="content has-text-centered">
                          <p><b-icon icon="upload" size="is-large"></b-icon></p>
                          <p>{{ importXmlFile ? importXmlFile.name : 'Drop the BOSS .itxml here for SCTE opt-outs' }}</p>
                        </div>
                      </section>
                    </b-upload>
                  </b-field>
                </div>
              </div>

              <div v-if="importResult" class="mt-4">
                <div
                  v-if="importResult.warnings && importResult.warnings.length"
                  class="notification is-warning is-light"
                >
                  <p v-for="(w, i) in importResult.warnings" :key="i" class="is-size-7 mb-1">
                    <b-icon icon="alert" size="is-small"></b-icon> {{ w }}
                  </p>
                </div>
                <div class="notification" :class="importResult.airReady ? 'is-success' : 'is-warning'">
                  <strong>
                    <template v-if="importResult.airReady">Air-ready ✓</template>
                    <template v-else>{{ conflictErrorCount }} error(s) — not air-ready</template>
                  </strong>
                  <span v-if="conflictWarningCount > 0" class="tag is-warning is-light ml-2">
                    {{ conflictWarningCount }} title/S/E warning(s) — advisory
                  </span>
                  <span v-if="importResult.playlistDate" class="tag is-dark ml-2">Playlist date: {{ importResult.playlistDate }}</span>
                  <br />
                  {{ importResult.stats.emitted }} items in playlist ·
                  <strong>{{ importResult.stats.needsFix }}</strong> NEEDS FIX (show segments) ·
                  <strong>{{ importResult.stats.unverifiedShortform }}</strong> unverified shortform ·
                  {{ importResult.stats.scte }} SCTE markers ·
                  <strong>{{ importResult.stats.scteApplied }}</strong> opt-outs (type C)
                  <br />
                  <span class="is-size-7">
                    Every item is in the playlist (continuous timeline). NEEDS FIX placeholders hold the slot for
                    shows pending QC/tagging; unverified shortform is emitted under its BOSS name and will resolve
                    once loaded. Fix the conflicts below and re-process for an air-ready playlist.
                  </span>
                </div>

                <div v-if="importResult.conflicts.length" class="box" style="max-height: 280px; overflow-y: auto;">
                  <p class="has-text-weight-bold mb-2">
                    <span v-if="conflictErrorCount > 0">{{ conflictErrorCount }} error(s)</span>
                    <span v-if="conflictErrorCount > 0 && conflictWarningCount > 0"> · </span>
                    <span v-if="conflictWarningCount > 0">{{ conflictWarningCount }} warning(s)</span>
                    — fix errors and re-import; warnings flag possible BOSS-side issues to confirm:
                  </p>
                  <table class="table is-narrow is-fullwidth is-size-7">
                    <thead>
                      <tr><th>Type</th><th>Asset</th><th>Detail</th><th>×</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="(c, i) in sortedConflicts" :key="i">
                        <td><span class="tag is-small" :class="conflictClass(c.type)">{{ c.type }}</span></td>
                        <td>{{ c.asset }}</td>
                        <td>{{ c.message }}</td>
                        <td>{{ c.occurrences }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>

            <!-- ================= EXPORT (BMS day-view) ================= -->
            <template v-if="mode === 'export'">
            <!-- Export Type Selection -->
            <b-field label="Export Type">
              <b-radio-button v-model="exportType" native-value="single" type="is-primary">
                <span>Single Day</span>
              </b-radio-button>
              <b-radio-button v-model="exportType" native-value="range" type="is-primary">
                <span>Date Range</span>
              </b-radio-button>
            </b-field>

            <!-- Single Date Picker -->
            <BaseDatePicker
              v-if="exportType === 'single'"
              v-model="criteria.date"
              label="Date"
              :rules="'required'"
            >
            </BaseDatePicker>

            <!-- Date Range Pickers -->
            <div v-if="exportType === 'range'" class="columns">
              <div class="column is-6">
                <BaseDatePicker
                  v-model="criteria.startDate"
                  label="Start Date"
                  :rules="'required'"
                >
                </BaseDatePicker>
              </div>
              <div class="column is-6">
                <BaseDatePicker
                  v-model="criteria.endDate"
                  label="End Date"
                  :rules="'required'"
                >
                </BaseDatePicker>
              </div>
            </div>

            <!-- Export Format Selection -->
            <b-field label="Export Format">
              <b-select v-model="exportFormat" expanded>
                <option value="mcrlist">MCR List</option>
                <option value="bxf">BXF</option>
              </b-select>
            </b-field>

            <!-- Path Type Selection -->
            <b-field label="File Paths">
              <b-radio v-model="pathType" native-value="channel">
                <span>Channel Config Paths</span>
                <span class="help is-inline ml-2">(Primary — current setup)</span>
              </b-radio>
              <b-radio v-model="pathType" native-value="secondary" class="mt-2">
                <span>Channel Secondary Paths</span>
                <span class="help is-inline ml-2">(e.g. cloud VM / DR target)</span>
              </b-radio>
              <b-radio v-model="pathType" native-value="gateway" class="mt-2">
                <span>Gateway SMB Paths</span>
                <span class="help is-inline ml-2">(VersityGW/Storage Config)</span>
              </b-radio>
            </b-field>

            <p v-if="pathType === 'gateway'" class="help has-text-info">
              <b-icon icon="information" size="is-small"></b-icon>
              Gateway paths use the SMB share path configured in Storage Config with path mapping enabled.
            </p>
            <p v-if="pathType === 'secondary'" class="help has-text-info">
              <b-icon icon="information" size="is-small"></b-icon>
              Secondary paths use <code>channel.playout_storage_root_secondary</code>. Useful when exporting for a parallel cloud/DR playout.
            </p>

            <p v-if="exportType === 'range'" class="help">
              <b-icon icon="information" size="is-small"></b-icon>
              Date range exports help test day boundary handling (e.g., items at midnight crossing between days).
            </p>
            </template>
          </section>
          <footer class="modal-card-foot" style="display: block">
            <button class="button" type="button" @click="closeModal">
              Cancel
            </button>
            <!-- Export mode -->
            <button
              v-if="mode === 'export'"
              class="button is-primary"
              :class="{ 'is-loading': loading }"
              :disabled="loading"
              @click.prevent="passes(exportReport)"
            >
              Export
            </button>
            <!-- Import mode -->
            <button
              v-if="mode === 'import'"
              class="button is-primary"
              :class="{ 'is-loading': loading }"
              :disabled="loading || (!importCsvFile && !importXmlFile)"
              @click.prevent="processImport"
            >
              Process Playlist
            </button>
            <button
              v-if="mode === 'import' && importResult && importResult.xml"
              class="button is-info"
              @click.prevent="downloadImportedMcrList"
            >
              Download MCRList{{ importResult.airReady ? '' : ' (with conflicts)' }}
            </button>
            <button
              v-if="mode === 'import' && importResult && (
                (importResult.conflicts && importResult.conflicts.length) ||
                (importResult.resolvedShows && importResult.resolvedShows.length)
              )"
              class="button is-warning"
              @click.prevent="downloadConflictReport"
            >
              <b-icon icon="file-download" size="is-small"></b-icon>
              <span>Download Sign-off Report (CSV)</span>
            </button>
          </footer>
        </div>
      </form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { format } from 'date-fns'
import BaseDatePicker from '@/components/BaseDatePicker'
import ScheduleApi from '@/api/schedules'

export default {
  name: 'ExportPlayoutScheduleModal',
  components: {
    BaseDatePicker
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    channelId: {
      type: Number,
      default: null
    },
    date: {
      type: Date,
      default: null
    },
    channelName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      initialized: false,
      showModal: this.show,
      mode: 'export', // 'export' (BMS day-view) or 'import' (BOSS playlist)
      exportType: 'single', // 'single' or 'range'
      exportFormat: 'mcrlist', // 'mcrlist' or 'bxf'
      pathType: 'channel', // 'channel' for playout_storage_root, 'gateway' for SMB share path
      importCsvFile: null,
      importXmlFile: null,
      importResult: null,
      criteria: {
        date: this.date || new Date(),
        startDate: this.date || new Date(),
        endDate: this.date || new Date()
      }
    }
  },
  watch: {
    show() {
      this.showModal = this.show
      // Reset to single day mode when modal opens, defaulting to the current day view date
      if (this.show) {
        this.mode = 'export'
        this.exportType = 'single'
        this.importCsvFile = null
        this.importXmlFile = null
        this.importResult = null
        this.criteria.date = this.date || new Date()
        this.criteria.startDate = this.date || new Date()
        this.criteria.endDate = this.date || new Date()
      }
    }
  },
  computed: {
    // BOSS playlist import is enabled only for Urban Brew channels (phase 1).
    isUbChannel() {
      const n = String(this.channelName || '').toUpperCase()
      return ['DUMISA', '1GOSPEL', 'ONE GOSPEL'].includes(n)
    },
    // Split conflict counts by severity — errors block air-ready, warnings are advisory.
    conflictErrorCount() {
      const cs = (this.importResult && this.importResult.conflicts) || []
      return cs.filter(c => (c.severity || 'error') === 'error').length
    },
    conflictWarningCount() {
      const cs = (this.importResult && this.importResult.conflicts) || []
      return cs.filter(c => c.severity === 'warning').length
    },
    // Sort conflicts so errors render above warnings (warnings shouldn't bury blockers).
    sortedConflicts() {
      const cs = (this.importResult && this.importResult.conflicts) || []
      const rank = c => (c.severity === 'warning' ? 1 : 0)
      return cs.slice().sort((a, b) => rank(a) - rank(b))
    }
  },
  async created() {
    this.initialized = true
  },
  methods: {
    async exportReport() {
      try {
        this.loading = true
        // pathType is 'channel' (primary), 'secondary', or 'gateway'
        // Pass as pathSource; keep useGatewayPaths for backward-compat with backend
        const pathSource = this.pathType
        const useGatewayPaths = this.pathType === 'gateway'

        if (this.exportType === 'single') {
          // Single day export
          if (this.exportFormat === 'mcrlist') {
            await ScheduleApi.exportPlayout(
              this.channelId,
              this.criteria.date,
              this.channelName,
              { useGatewayPaths, pathSource }
            )
          } else {
            await ScheduleApi.exportBXF(
              this.channelId,
              this.criteria.date,
              this.channelName,
              { useGatewayPaths, pathSource }
            )
          }
        } else {
          // Date range export
          if (this.exportFormat === 'mcrlist') {
            await ScheduleApi.exportPlayoutRange(
              this.channelId,
              this.criteria.startDate,
              this.criteria.endDate,
              this.channelName,
              { useGatewayPaths, pathSource }
            )
          } else {
            await ScheduleApi.exportBXFRange(
              this.channelId,
              this.criteria.startDate,
              this.criteria.endDate,
              this.channelName,
              { useGatewayPaths, pathSource }
            )
          }
        }

        this.$buefy.toast.open({
          message: 'Export downloaded successfully',
          position: 'is-top',
          type: 'is-success'
        })
        this.closeModal()
      } catch (err) {
        console.log(err)
        this.$buefy.toast.open({
          message: 'Error exporting schedule',
          position: 'is-top',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },
    conflictClass(type) {
      // BLOCK-level (file/QC/tag missing) red; advisory (warnings) yellow.
      // Warnings come from sanity checks that don't block air-ready.
      const warningTypes = ['SEG_COUNT', 'SEG_COUNT_MISMATCH', 'TITLE_MISMATCH', 'SE_MISMATCH']
      return warningTypes.includes(type) ? 'is-warning' : 'is-danger'
    },
    async processImport() {
      if (!this.importCsvFile && !this.importXmlFile) return
      try {
        this.loading = true
        this.importResult = null
        const csvContent = this.importCsvFile ? await this.importCsvFile.text() : null
        const xmlContent = this.importXmlFile ? await this.importXmlFile.text() : null
        // Honour the "File Paths" radio for BOSS too — same toggle the
        // normal export uses. Backend resolves the gateway storage config
        // when true and hands the smb_share_path to the converter as
        // the playout base path.
        const pathSource = this.pathType
        const useGatewayPaths = this.pathType === 'gateway'
        this.importResult = await ScheduleApi.importBossPlaylist(this.channelId, { csvContent, xmlContent, useGatewayPaths, pathSource })
        this.$buefy.toast.open({
          message: this.importResult.airReady
            ? 'Playlist processed — air-ready'
            : `Playlist processed — ${this.importResult.conflicts.length} conflict(s) to resolve`,
          position: 'is-top',
          type: this.importResult.airReady ? 'is-success' : 'is-warning'
        })
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Error processing BOSS playlist'
        this.$buefy.toast.open({ message: msg, position: 'is-top', type: 'is-danger' })
      } finally {
        this.loading = false
      }
    },
    // ddMMyy stamp for download filenames — uses the playlist's broadcast date
    // (the day being scheduled, from the uploaded BOSS file), NOT today's date.
    playlistStamp() {
      const d = this.importResult && this.importResult.playlistDate
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [y, m, day] = d.split('-')
        return `${day}${m}${y.slice(2)}`
      }
      return format(new Date(), 'ddMMyy')
    },
    downloadImportedMcrList() {
      if (!this.importResult || !this.importResult.xml) return
      const blob = new Blob([this.importResult.xml], { type: 'application/xml' })
      const fileURL = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = fileURL
      const stamp = this.playlistStamp()
      link.setAttribute('download', `MCRList-${encodeURIComponent(this.channelName || this.importResult.channel)}-BOSS-${stamp}.MCRLIST`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    downloadConflictReport() {
      const r = this.importResult
      if (!r) return
      const esc = (v) => {
        const s = String(v == null ? '' : v)
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }
      const row = (cols) => cols.map(esc).join(',')
      const s = r.stats || {}
      const lines = []
      // Summary block
      lines.push(row(['BOSS Playlist Sign-off Report']))
      lines.push(row(['Channel', r.channel || this.channelName || '']))
      lines.push(row(['Playlist date', r.playlistDate || '(unknown)']))
      lines.push(row(['Generated', new Date().toLocaleString()]))
      lines.push(row(['Air-ready', r.airReady ? 'YES' : 'NO']))
      lines.push(row(['Items emitted', s.emitted]))
      lines.push(row(['NEEDS FIX (show segments)', s.needsFix]))
      lines.push(row(['Unverified shortform', s.unverifiedShortform]))
      lines.push(row(['SCTE markers', s.scte]))
      lines.push(row(['Opt-outs (type C)', s.scteApplied]))
      lines.push('')
      // Warnings block
      if (r.warnings && r.warnings.length) {
        lines.push(row(['Warnings']))
        r.warnings.forEach(w => lines.push(esc(w)))
        lines.push('')
      }
      // Conflicts table — errors first, then warnings (matches the on-screen order)
      lines.push(row(['Type', 'Severity', 'Asset', 'Detail', 'Occurrences']))
      ;(this.sortedConflicts || []).forEach(c => lines.push(
        row([c.type, c.severity || 'error', c.asset, c.message, c.occurrences])
      ))
      lines.push('')
      // Resolved shows — the sign-off list. Operators eyeball this to confirm
      // every TAG_S#_E# code resolved to the right BMS title + tx_filename.
      // `airings` = how many times the show airs in the day (occurrences / total BMS segs).
      lines.push(row(['Resolved Shows (sign-off list)']))
      lines.push(row(['Code', 'Season', 'Episode', 'BOSS Title', 'BMS Title', 'Title match %', 'TX Filename', 'BMS Segments', 'Occurrences', 'Airings']))
      ;(r.resolvedShows || []).forEach(rs => lines.push(row([
        rs.code, rs.season, rs.episode, rs.bossTitle, rs.bmsTitle,
        rs.similarity != null ? Math.round(rs.similarity * 100) + '%' : '',
        rs.txFilename, rs.totalBmsSegs, rs.occurrences,
        rs.airings != null ? rs.airings.toFixed(1) : ''
      ])))
      // BOM so Excel reads UTF-8 (asset names carry accents / special chars)
      const csv = '﻿' + lines.join('\r\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const fileURL = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = fileURL
      const stamp = this.playlistStamp()
      link.setAttribute('download', `BOSS-signoff-${encodeURIComponent(this.channelName || r.channel)}-${stamp}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    closeModal() {
      this.showModal = false
      this.$emit('close-modal')
    }
  }
}
</script>
